import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useCancelConsignmentInvoice, useConsignmentInvoice, useCreateShipmentFromInvoice, useShipment, useShipmentAction,
  useUpdateConsignmentInvoiceLines, useUpdateShipmentDetails,
} from '../api/hooks';
import { describeError } from '../api/errors';
import { trimDecimal } from '../format';
import { useSession } from '../auth/session';
import type { ConsignmentInvoiceState, ShipmentState } from '../api/types';
import { shipmentStateLabel } from './shipment-state';
import {
  draftTotals, hasUnsavedQuantities, isPositive, lineAmount, parseCharge,
} from './consignment-invoice/model';
import { isZeroAmount, PAYABLE_TONE, payableStatusKey } from './despatch-payable';
import { QuantityStepper } from './consignment-invoice/QuantityStepper';
import { Alert, Button, Chip, Field, Money, Note, PageHeader, Panel, Spinner, Table, type Tone } from '../ui';

/*
 * A `Record` over the const union on purpose, and it must stay one. SH-14's terminal state (D92,
 * still open) is a fourth member of `consignmentInvoiceStates`, and a `Record` FAILS TYPECHECK the
 * day it is added — which forces a deliberate choice of colour instead of a silent default. An
 * index signature or a `Partial` here would compile and show the new state untoned.
 *
 * There is deliberately no label map beside it: a label is `t(`consignmentInvoice.state.${state}`)`,
 * so the new state is one line in `packages/contracts` plus one key in `en.ts` and no edit here.
 */
const STATE_TONE: Record<ConsignmentInvoiceState, Tone> = { OPEN: 'them', SHIPMENT_CREATED: 'us', CANCELLED: 'lapse' };
const SHIPMENT_TONE: Record<ShipmentState, Tone> = {
  DRAFT: 'them', CONFIRMED: 'us', PACKED: 'us', AWAITING_PAYMENT: 'stop', DISPATCHED: 'run', DELIVERED: 'done', CANCELLED: 'lapse',
};
/* Dispatch is the only staff action: it packs the shipment first if it is still a draft. DISPATCHED has no
   next staff action: Delivered is the retailer's "Acknowledge receipt", never Admin's. */
const CAN_DISPATCH: ReadonlySet<ShipmentState> = new Set<ShipmentState>(['DRAFT', 'PACKED', 'AWAITING_PAYMENT']);

/** A row in a money summary: the label on the left, the figure on the right. */
function Line({ k, children, strong }: { k: string; children: React.ReactNode; strong?: boolean }) {
  return (
    <div className={`mk-pi-line ${strong ? 'mk-pi-strong' : ''}`}>
      <span>{k}</span>
      <span>{children}</span>
    </div>
  );
}

/**
 * The Consignment Invoice page (docs/plans/consignment-invoice-parent §7): orders you can expand, quantities you can change with the
 * amounts following at once, the terms, the charges, and the shipment created from it. Server
 * figures are the truth; while quantities are being edited the page shows the same arithmetic
 * locally and says so, and replaces it on save.
 */
export function ConsignmentInvoiceDetailScreen() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: pi, isLoading, error } = useConsignmentInvoice(id);
  const save = useUpdateConsignmentInvoiceLines(id ?? '');
  const cancel = useCancelConsignmentInvoice(id ?? '');
  const createShipment = useCreateShipmentFromInvoice(id ?? '');
  // Both are told the invoice as well as the shipment: the shipment is drawn from this invoice's
  // payload, so the invoice is what has to be re-read before Dispatch or Save comes back.
  const act = useShipmentAction(pi?.shipment?.id ?? '', id);
  const updateShipment = useUpdateShipmentDetails(pi?.shipment?.id ?? '', id);
  const { me } = useSession();
  const navigate = useNavigate();

  // The quantity chosen, by order line. A line not in here shows its saved quantity.
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [openOrders, setOpenOrders] = useState<Set<string>>(new Set());
  const [courierName, setCourierName] = useState('');
  const [courierCharge, setCourierCharge] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [reason, setReason] = useState('');
  /* Editing the travel details of an existing shipment; null when not editing.
     The CHARGE is one of them (SH-02): "admin should be able to edit shipping details because
     for shipping PI bill is important" — the bill is purchase amount plus courier charge, so a
     charge that could be set once and never corrected was the one field that mattered most. */
  const [shipEdit, setShipEdit] = useState<{ carrier: string; charge: string; tracking: string } | null>(null);
  const [shipEditError, setShipEditError] = useState<string | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  // Shown inside the Shipment panel, beside the buttons that caused it, because that panel is
  // at the foot of a long page and a message at the top would go unseen.
  const [shipmentError, setShipmentError] = useState<string | null>(null);

  const totals = useMemo(
    () => (pi ? draftTotals(pi.orders, quantities, pi.figures.courierCharge) : null),
    [pi, quantities],
  );

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!pi || !totals) return null;

  const editable = pi.state === 'OPEN';
  const unsaved = hasUnsavedQuantities(pi.orders, quantities);
  const canManageShipment = me?.kind === 'internal' && (me.isSuperAdmin || me.permissions.includes('shipment.manage'));
  /*
   * The server's figures are the only payable there is. The draft below recomputes what a typed
   * quantity moves — the value of the goods — and deliberately computes no payable at all: what
   * is owed on a despatch is the courier charge (SH-13) and does not depend on quantity. This
   * page used to derive its own `totalPayable` and `Still to pay` in the browser, which is how
   * Admin and the retailer came to show the same invoice differently.
   */
  const f = pi.figures;
  const nothingPayable = isZeroAmount(f.totalPayable);
  const inCredit = f.remaining.startsWith('-');

  const qtyOf = (lineId: string, saved: number) => quantities[lineId] ?? saved;
  const setQty = (lineId: string, q: number) => {
    setFailed(null);
    setQuantities((prev) => ({ ...prev, [lineId]: q }));
  };
  const toggleOrder = (orderId: string) => setOpenOrders((prev) => {
    const next = new Set(prev);
    if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
    return next;
  });

  async function saveQuantities() {
    if (!pi) return;
    setFailed(null);
    const lines = pi.orders.flatMap((o) => o.lines)
      .filter((l) => (quantities[l.orderLineId] ?? l.quantity) !== l.quantity)
      .map((l) => ({ orderLineId: l.orderLineId, quantity: quantities[l.orderLineId]! }));
    try {
      await save.mutateAsync(lines);
      setQuantities({});
    } catch (e) {
      setFailed(describeError(e));
    }
  }

  async function makeShipment() {
    setShipmentError(null);
    const charge = parseCharge(courierCharge);
    if (!courierName.trim()) { setShipmentError('Courier name is required.'); return; }
    if (charge === null) { setShipmentError('Courier charge is required. Use a number such as 120.00, or 0 if there is none.'); return; }
    try {
      await createShipment.mutateAsync({
        courierName: courierName.trim(), courierCharge: charge, trackingNumber: trackingNumber.trim() || undefined,
      });
      setCourierName(''); setCourierCharge(''); setTrackingNumber('');
    } catch (e) {
      setShipmentError(describeError(e));
    }
  }

  async function runShipment(action: 'dispatch' | 'cancel', withReason?: string) {
    setShipmentError(null);
    try {
      await act.mutateAsync({ action, reason: withReason });
      setReason('');
    } catch (e) {
      setShipmentError(describeError(e));
    }
  }

  async function saveShipmentDetails() {
    if (!shipEdit || !sh) return;
    setShipEditError(null);
    const carrier = shipEdit.carrier.trim();
    if (!carrier) { setShipEditError('Courier name cannot be empty.'); return; }
    // Same rule as creating the shipment: a figure, with 0 for "the courier charged nothing".
    // The route can clear the charge to null, but this form never asks for that — a blank
    // charge on a bill reads as an oversight, and 0 is the answer that says it was none.
    const charge = parseCharge(shipEdit.charge);
    if (charge === null) {
      setShipEditError('Courier charge is required. Use a number such as 120.00, or 0 if there is none.');
      return;
    }
    try {
      await updateShipment.mutateAsync({
        carrier, courierCharge: charge, trackingReference: shipEdit.tracking.trim() || null,
      });
      setShipEdit(null);
    } catch (e) {
      // The typed values stay in the form so nothing has to be re-entered.
      setShipEditError(describeError(e));
    }
  }

  async function cancelInvoice() {
    setFailed(null);
    try {
      await cancel.mutateAsync(reason.trim());
      setReason(''); setShowCancel(false);
    } catch (e) {
      setFailed(describeError(e));
    }
  }

  const sh = pi.shipment;

  return (
    <>
      {/* This screen already had its way back, but inside the subtitle, between the
          retailer and the currency — a navigation control filed among the facts about
          the record. It moves here so that every detail screen puts it in the one place,
          and the subtitle goes back to being only what this invoice IS. */}
      <div className="mk-backlink">
        <Link to="/consignment-invoices">&larr; All consignment invoices</Link>
      </div>
      <PageHeader
        title={pi.reference}
        sub={`${pi.retailer.name} · ${pi.currency}`}
        actions={(
          <span style={{ display: 'inline-flex', gap: 'var(--mk-space-2)' }}>
            {/* Once a shipment exists, the invoice's headline is where that shipment is — "Shipment created"
                is true forever and says nothing about dispatch or delivery. */}
            {pi.shipment && pi.state !== 'CANCELLED'
              ? <Chip tone={SHIPMENT_TONE[pi.shipment.state]}>{shipmentStateLabel(pi.shipment.state)}</Chip>
              : <Chip tone={STATE_TONE[pi.state]}>{t(`consignmentInvoice.state.${pi.state}`)}</Chip>}
            {pi.state !== 'CANCELLED' ? <Chip tone={PAYABLE_TONE[f.paymentStatus]}>{t(payableStatusKey(f.paymentStatus))}</Chip> : null}
          </span>
        )}
      />
      {failed ? <Alert tone="stop"><span className="mk-lines">{failed}</span></Alert> : null}
      {pi.state === 'CANCELLED' ? (
        <Alert tone="lapse">This consignment invoice was cancelled{pi.cancelReason ? `: ${pi.cancelReason}` : '.'}</Alert>
      ) : null}
      <Panel
        title={`Orders (${pi.orders.length})`}
        actions={editable ? (
          <span className="mk-dim">{unsaved ? 'Unsaved quantity changes' : 'Expand an order to change quantities'}</span>
        ) : undefined}
      >
        {pi.orders.map((o) => {
          const isOpen = openOrders.has(o.orderId);
          const live = totals.perOrder.find((p) => p.orderId === o.orderId);
          return (
            <div key={o.orderId}>
              <div className="mk-pi-order-head">
                <button
                  type="button"
                  className="mk-pi-twisty"
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${o.reference}`}
                  onClick={() => toggleOrder(o.orderId)}
                >
                  {isOpen ? '▼' : '▶'}
                </button>
                <h3><Link to={`/orders/${o.orderId}`}>{o.reference}</Link></h3>
                <span className="mk-dim">{pi.retailer.name}</span>
                <span className="mk-spacer" />
                <span className="mk-dim">{live?.products ?? 0} product{live?.products === 1 ? '' : 's'}</span>
                <b><Money amount={live?.orderAmount ?? o.orderAmount} currency={pi.currency} /></b>
              </div>
              {isOpen ? (
                <div>
                  <Table
                    rows={o.lines}
                    rowKey={(l) => l.orderLineId}
                    columns={[
                      { header: 'Item / Product Name', render: (l) => l.description ?? '—' },
                      { header: 'Variant', render: (l) => <code>{l.variantCode}</code> },
                      {
                        header: 'Quantity',
                        numeric: true,
                        render: (l) => {
                          const q = qtyOf(l.orderLineId, l.quantity);
                          return editable ? (
                            <QuantityStepper
                              label={l.description ?? 'piece'}
                              value={q}
                              max={l.maxQuantity}
                              onChange={(next) => setQty(l.orderLineId, next)}
                            />
                          ) : q;
                        },
                      },
                      { header: 'Unit price', numeric: true, render: (l) => <Money amount={l.unitPrice} currency={pi.currency} /> },
                      {
                        header: 'Purchase amount', numeric: true,
                        render: (l) => <Money amount={lineAmount(l.unitPrice, qtyOf(l.orderLineId, l.quantity))} currency={pi.currency} />,
                      },
                    ]}
                  />
                  <div className="mk-pbody" style={{ textAlign: 'right' }}>
                    Updated order amount: <b><Money amount={live?.orderAmount ?? o.orderAmount} currency={pi.currency} /></b>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
        {editable ? (
          <div className="mk-pbody" style={{ display: 'flex', gap: 'var(--mk-space-3)', justifyContent: 'flex-end' }}>
            <Button tone="ghost" disabled={!unsaved} onClick={() => { setQuantities({}); setFailed(null); }}>Discard changes</Button>
            <Button disabled={!unsaved || save.isPending} onClick={() => void saveQuantities()}>Save quantities</Button>
          </div>
        ) : null}
      </Panel>

      <div className="mk-pi-split">
        <Panel title="Consignment invoice summary" pad>
          <Line k="Total orders">{pi.orders.length}</Line>
          <Line k="Total products">{totals.products}</Line>
          <Line k={t('despatch.consignedValue')} strong>
            <Money amount={totals.consignedValue} currency={pi.currency} />
          </Line>
          <Line k={t('despatch.courierCharge')}>
            {f.courierCharge === null ? <span className="mk-dim">{t('despatch.courierPending')}</span>
              : <Money amount={f.courierCharge} currency={pi.currency} />}
          </Line>
          {unsaved ? (
            <p className="mk-dim">
              Shows your unsaved quantities. The saved value is{' '}
              <Money amount={f.consignedValue} currency={pi.currency} />.
            </p>
          ) : null}
        </Panel>

        {/* The same block the retailer sees, from the same `payableView` on the server: the goods
            are consigned and are not a debt (SH-06), and only the courier charge is payable
            (SH-13). It used to read "Purchase amount · Courier charge · Total payable" and billed
            a consignment retailer for stock they do not own. */}
        <Panel title={t('despatch.panelTitle')} pad>
          <Line k={t('despatch.consignedValue')}><Money amount={f.consignedValue} currency={pi.currency} /></Line>
          <Note>{t('despatch.titleNote')}</Note>
          <Line k={t('despatch.courierCharge')}>
            {f.courierCharge === null ? <span className="mk-dim">{t('despatch.courierPending')}</span>
              : <Money amount={f.courierCharge} currency={pi.currency} />}
          </Line>
          <Line k={t('despatch.tax')}><span className="mk-dim">{t('despatch.taxNote')}</span></Line>
          <Line k={t('despatch.payable')} strong>
            {nothingPayable
              ? <span className="mk-dim">{t('despatch.nothingPayable')}</span>
              : <Money amount={f.totalPayable} currency={pi.currency} />}
          </Line>
          {nothingPayable ? null : (
            <>
              <Line k={t('despatch.paid')}><Money amount={f.payableReceived} currency={pi.currency} /></Line>
              <Line k={inCredit ? t('despatch.credit') : t('despatch.stillToPay')} strong>
                <Money amount={inCredit ? f.remaining.slice(1) : f.remaining} currency={pi.currency} />
              </Line>
            </>
          )}
          {isZeroAmount(f.goodsPaymentsReceived) ? null : (
            <>
              <Line k={t('despatch.goodsPayments')}><Money amount={f.goodsPaymentsReceived} currency={pi.currency} /></Line>
              <Note>{t('despatch.goodsPaymentsNote')}</Note>
            </>
          )}
        </Panel>
      </div>

      <Panel title="Shipment" pad>
        {shipmentError ? <Alert tone="stop"><span className="mk-lines">{shipmentError}</span></Alert> : null}
        {sh ? (
          <>
            <Line k="Shipment">
              {sh.reference} <Chip tone={SHIPMENT_TONE[sh.state]}>{shipmentStateLabel(sh.state)}</Chip>
            </Line>
            <Line k="Courier name">
              {shipEdit ? (
                <input className="mk-fld" aria-label="Courier name" maxLength={120} value={shipEdit.carrier}
                  onChange={(e) => setShipEdit({ ...shipEdit, carrier: e.target.value })} />
              ) : (sh.courierName ?? '—')}
            </Line>
            <Line k="Courier charge">
              {shipEdit ? (
                <input className="mk-fld" aria-label="Courier charge" inputMode="decimal" placeholder="0.00"
                  value={shipEdit.charge} onChange={(e) => setShipEdit({ ...shipEdit, charge: e.target.value })} />
              ) : <Money amount={sh.courierCharge} currency={pi.currency} />}
            </Line>
            {pi.courier.scheduled && pi.courier.charge !== null ? (
              <>
                <Line k="Courier charge paid"><Money amount={pi.courier.paid} currency={pi.currency} /></Line>
                {isPositive(pi.courier.pending) ? (
                  <Line k="Courier payment awaiting verification">
                    <Money amount={pi.courier.pending} currency={pi.currency} />{' '}
                    <Link to={`/orders/${pi.courier.anchorOrderId}`}>Verify it on the order</Link>
                  </Line>
                ) : null}
                <Line k="Courier charge outstanding" strong>
                  {pi.courier.blocksDispatch
                    ? <Chip tone="stop"><Money amount={pi.courier.outstanding} currency={pi.currency} /> — blocks dispatch</Chip>
                    : <Chip tone="done">Paid</Chip>}
                </Line>
              </>
            ) : null}
            <Line k="Tracking number">
              {shipEdit ? (
                <input className="mk-fld" aria-label="Tracking number" maxLength={120} value={shipEdit.tracking}
                  onChange={(e) => setShipEdit({ ...shipEdit, tracking: e.target.value })} />
              ) : (sh.trackingNumber ?? '—')}
            </Line>
            <Line k="Dispatched">{sh.dispatchedAt ? new Date(sh.dispatchedAt).toLocaleString() : '—'}</Line>
            <Line k="Delivered">{sh.deliveredAt ? new Date(sh.deliveredAt).toLocaleString() : '—'}</Line>
            {sh.state === 'DISPATCHED' ? (
              <Note>Shipped. It becomes Delivered when the retailer acknowledges receipt on their Orders page.</Note>
            ) : null}
            {shipEditError ? <Alert tone="stop">{shipEditError}</Alert> : null}
            {canManageShipment && sh.state !== 'CANCELLED' ? (
              <div style={{ display: 'flex', gap: 'var(--mk-space-3)', marginTop: 'var(--mk-space-3)' }}>
                {shipEdit ? (
                  <>
                    <Button disabled={updateShipment.isPending} onClick={() => void saveShipmentDetails()}>
                      {updateShipment.isPending ? 'Saving…' : 'Save'}
                    </Button>
                    <Button tone="ghost" disabled={updateShipment.isPending} onClick={() => { setShipEdit(null); setShipEditError(null); }}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    tone="ghost"
                    onClick={() => {
                      setShipEditError(null);
                      setShipEdit({
                        carrier: sh.courierName ?? '',
                        // "120.1000" is what NUMERIC(19,4) stores; "120.1" is what Admin typed.
                        charge: sh.courierCharge === null ? '' : trimDecimal(sh.courierCharge),
                        tracking: sh.trackingNumber ?? '',
                      });
                    }}
                  >
                    Edit
                  </Button>
                )}
              </div>
            ) : null}
            {CAN_DISPATCH.has(sh.state) ? (
              <div style={{ display: 'flex', gap: 'var(--mk-space-3)', alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 'var(--mk-space-3)' }}>
                <Field label="Reason" hint="Needed to cancel the shipment.">
                  <input className="mk-fld" value={reason} onChange={(e) => setReason(e.target.value)} />
                </Field>
                <Button disabled={act.isPending || shipEdit !== null} onClick={() => void runShipment('dispatch', reason.trim() || undefined)}>
                  Dispatch
                </Button>
                {sh.state === 'DRAFT' || sh.state === 'PACKED' ? (
                  <Button tone="ghost" disabled={act.isPending || reason.trim().length < 3} onClick={() => void runShipment('cancel', reason.trim())}>
                    Cancel shipment
                  </Button>
                ) : null}
              </div>
            ) : null}
          </>
        ) : editable ? (
          <>
            {!pi.canCreateShipment && pi.cannotCreateReason ? <Alert tone="us">{pi.cannotCreateReason}</Alert> : null}
            {unsaved ? <Alert tone="us">Save your quantity changes before creating the shipment.</Alert> : null}
            <div style={{ display: 'grid', gap: 'var(--mk-space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <Field label="Courier name">
                <input className="mk-fld" value={courierName} onChange={(e) => setCourierName(e.target.value)} />
              </Field>
              <Field label={`Courier charge (${pi.currency})`} hint="Use 0 if there is none.">
                <input className="mk-fld" inputMode="decimal" placeholder="0.00" value={courierCharge} onChange={(e) => setCourierCharge(e.target.value)} />
              </Field>
              <Field label="Tracking number" hint="Optional.">
                <input className="mk-fld" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} />
              </Field>
            </div>
            <div style={{ marginTop: 'var(--mk-space-3)' }}>
              <Button disabled={!pi.canCreateShipment || unsaved || createShipment.isPending} onClick={() => void makeShipment()}>
                Create Shipment
              </Button>
            </div>
          </>
        ) : (
          <span className="mk-dim">No shipment was created from this consignment invoice.</span>
        )}
      </Panel>

      {editable ? (
        <Panel title="Cancel this consignment invoice" pad>
          {showCancel ? (
            <div style={{ display: 'flex', gap: 'var(--mk-space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Field label="Reason" hint="The quantity goes back to be invoiced again.">
                <input className="mk-fld" value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              <Button tone="danger" disabled={cancel.isPending || reason.trim().length < 3} onClick={() => void cancelInvoice()}>
                Confirm cancel
              </Button>
              <Button tone="ghost" onClick={() => { setShowCancel(false); setReason(''); }}>Keep it</Button>
            </div>
          ) : (
            <Button tone="ghost" onClick={() => setShowCancel(true)}>Cancel consignment invoice</Button>
          )}
        </Panel>
      ) : null}
      <div style={{ height: 'var(--mk-space-5)' }} />
      <Button tone="ghost" onClick={() => void navigate('/consignment-invoices')}>Back to consignment invoices</Button>
    </>
  );
}

/**
 * Old `/shipments/:id` links (emails, bookmarks, the order page) land here and are
 * sent to the consignment invoice the shipment was created from.
 *
 * `consignmentInvoiceId` is the WIRE field name and is frozen in this release: a drizzle column
 * property cannot be aliased, so `consignmentInvoiceId` and its ~110 call sites move together in
 * D97 Wave 3 (expand, deploy, contract).
 */
export function ShipmentRedirect() {
  const { id } = useParams<{ id: string }>();
  const { data: s, isLoading, error } = useShipment(id);
  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!s) return null;
  if (s.consignmentInvoiceId) return <Navigate to={`/consignment-invoices/${s.consignmentInvoiceId}`} replace />;
  return (
    <Alert tone="stop">
      {s.reference} has no consignment invoice, so there is nothing to show it in. <Link to="/consignment-invoices">Go to consignment invoices</Link>
    </Alert>
  );
}

